/**
 * ── THE FETCH SCHEDULER — lanes, concurrency, priority, cancellation AS DATA (plan §4.3) ─────────
 *
 * There is no difference between a boot-time and a mid-play request except its LANE. The
 * scheduler turns "N handles want bytes" into "which fetch starts next" under a per-lane
 * concurrency (injected: `{ boot: 8, gameplay: 4, prefetch: 2 }` by default) and a strict lane
 * priority (`boot` > `gameplay` > `prefetch`), FIFO inside a lane.
 *
 * ⚠️ CANCELLATION IS A STATE, NOT AN EVENT. `cancel(id)` marks the request; `next()` never returns
 * a cancelled request, and `complete(id)` on one is a no-op. That is what lets `release()` before
 * `ready` (§4.2) be a single write with no race against a `next()` already in flight: the
 * request the service is about to start is either `queued` when `next()` looks, or it is not.
 *
 * ⚠️ PROMOTION, NOT DUPLICATION. A second `enqueue` for an id already queued in a LESS urgent
 * lane moves it to the new lane (at the BACK of that lane's FIFO — it is a new request there); a
 * second enqueue in the same or a less urgent lane is a no-op that says so. A `done`/`cancelled`
 * id may be enqueued again — it is a fresh request with a fresh `seq`. An `active` request is
 * never re-laned (the fetch is already running).
 *
 * PURE (`PURE_DIRS`): one plain object, mutated in place, deterministic (`seq` is the only order).
 */

import { ASSET_LANES, laneOutranks, type AssetLane } from './types';

export interface SchedulerConfig {
  concurrency: Record<AssetLane, number>;
}

export const DEFAULT_SCHEDULER_CONFIG: SchedulerConfig = {
  concurrency: { boot: 8, gameplay: 4, prefetch: 2 },
};

export type RequestState = 'queued' | 'active' | 'done' | 'cancelled';

export interface SchedulerRequest {
  id: string;
  lane: AssetLane;
  /** Monotonic enqueue order — the FIFO key. Re-laning assigns a new one. */
  seq: number;
  state: RequestState;
}

export interface SchedulerState {
  config: SchedulerConfig;
  nextSeq: number;
  requests: Record<string, SchedulerRequest>;
  /** Fetches in flight per lane — what `concurrency` is compared against. */
  active: Record<AssetLane, number>;
}

export function createScheduler(config: SchedulerConfig = DEFAULT_SCHEDULER_CONFIG): SchedulerState {
  return {
    config: { concurrency: { ...config.concurrency } },
    nextSeq: 1,
    requests: {},
    active: { boot: 0, gameplay: 0, prefetch: 0 },
  };
}

export interface EnqueueResult {
  ok: true;
  /** `queued` new · `promoted` re-laned to a more urgent lane · `duplicate` already there, untouched. */
  outcome: 'queued' | 'promoted' | 'duplicate';
  request: SchedulerRequest;
}

export function enqueue(state: SchedulerState, req: { id: string; lane: AssetLane }): EnqueueResult {
  const held = state.requests[req.id];
  if (held !== undefined && (held.state === 'queued' || held.state === 'active')) {
    if (held.state === 'queued' && laneOutranks(req.lane, held.lane)) {
      held.lane = req.lane;
      held.seq = state.nextSeq++;
      return { ok: true, outcome: 'promoted', request: held };
    }
    return { ok: true, outcome: 'duplicate', request: held };
  }
  const fresh: SchedulerRequest = { id: req.id, lane: req.lane, seq: state.nextSeq++, state: 'queued' };
  state.requests[req.id] = fresh;
  return { ok: true, outcome: 'queued', request: fresh };
}

/**
 * The next fetch to start, or `null` when every lane with work is at its concurrency. Marks it
 * `active`. Most urgent lane first; within a lane, lowest `seq`. Call repeatedly to fill every
 * free slot (`drain`).
 */
export function next(state: SchedulerState): SchedulerRequest | null {
  for (const lane of ASSET_LANES) {
    if (state.active[lane] >= state.config.concurrency[lane]) continue;
    let best: SchedulerRequest | null = null;
    for (const r of Object.values(state.requests)) {
      if (r.state !== 'queued' || r.lane !== lane) continue;
      if (best === null || r.seq < best.seq) best = r;
    }
    if (best !== null) {
      best.state = 'active';
      state.active[lane] += 1;
      return best;
    }
  }
  return null;
}

/** Every fetch that may start now, in start order. Empties the free slots in one call. */
export function drain(state: SchedulerState): SchedulerRequest[] {
  const out: SchedulerRequest[] = [];
  for (let r = next(state); r !== null; r = next(state)) out.push(r);
  return out;
}

/** The fetch finished (however it ended). Frees the lane slot. `false` when it was not active. */
export function complete(state: SchedulerState, id: string): boolean {
  const r = state.requests[id];
  if (r === undefined || r.state !== 'active') return false;
  r.state = 'done';
  state.active[r.lane] -= 1;
  return true;
}

export interface CancelResult {
  ok: boolean;
  /** What it was: `queued` (never started — nothing to abort) · `active` (the caller aborts the fetch). */
  was: RequestState | 'unknown';
}

/** A cancelled request never becomes `next()`; an active one frees its slot and the caller aborts. */
export function cancel(state: SchedulerState, id: string): CancelResult {
  const r = state.requests[id];
  if (r === undefined) return { ok: false, was: 'unknown' };
  const was = r.state;
  if (was === 'done' || was === 'cancelled') return { ok: false, was };
  if (was === 'active') state.active[r.lane] -= 1;
  r.state = 'cancelled';
  return { ok: true, was };
}

/** Drop settled requests (`done`/`cancelled`) so the table does not grow for the session's life. */
export function prune(state: SchedulerState): number {
  let n = 0;
  for (const [id, r] of Object.entries(state.requests)) {
    if (r.state === 'done' || r.state === 'cancelled') {
      delete state.requests[id];
      n += 1;
    }
  }
  return n;
}

export interface LaneCounts {
  queued: number;
  active: number;
}

export interface SchedulerCounts {
  byLane: Record<AssetLane, LaneCounts>;
  queued: number;
  active: number;
}

export function schedulerCounts(state: SchedulerState): SchedulerCounts {
  const byLane: Record<AssetLane, LaneCounts> = {
    boot: { queued: 0, active: 0 },
    gameplay: { queued: 0, active: 0 },
    prefetch: { queued: 0, active: 0 },
  };
  let queued = 0;
  let active = 0;
  for (const r of Object.values(state.requests)) {
    if (r.state === 'queued') {
      byLane[r.lane].queued += 1;
      queued += 1;
    } else if (r.state === 'active') {
      byLane[r.lane].active += 1;
      active += 1;
    }
  }
  return { byLane, queued, active };
}
